import { Button, Table } from 'react-bootstrap';
import { ExternalLink, Printer, Truck } from 'lucide-react';
import type { FedExShipmentTracking, TrackingMoreDetail } from '@/models/FedExTrackingDetail';
import { TrackingMoreTimeline } from './TrackingMoreTimeline';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

interface FedExShipmentCardProps {
  shippingData: FedExShipmentTracking;
  trackingMoreData?: TrackingMoreDetail;
  isDownloadingLabel: boolean;
  onDownloadLabel: () => void;
  onDownloadOriginalLabel?: () => void;
}

export const formatUnifiedShippingStatus = (status?: string): string => {
  if (!status) {
    return '-';
  }

  return status
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const buildFedExTrackingUrl = (trackingNumber: string): string =>
  `https://www.fedex.com/fedextrack/?trknbr=${encodeURIComponent(trackingNumber)}`;

const formatTimestamp = (timestamp?: string): string => {
  if (!timestamp) {
    return '-';
  }

  const parsedTimestamp = new Date(timestamp);
  return Number.isNaN(parsedTimestamp.getTime()) ? timestamp : parsedTimestamp.toLocaleString();
};

export function FedExShipmentCard({
  shippingData,
  trackingMoreData = shippingData.trackingMoreData,
  isDownloadingLabel,
  onDownloadLabel,
  onDownloadOriginalLabel,
}: FedExShipmentCardProps) {
  const trackingNumber = shippingData.trackingNumber || shippingData.providerOrderId;
  const matchingTracking =
    trackingMoreData?.courierCode === 'fedex' &&
    trackingMoreData.trackingNumber === trackingNumber &&
    trackingMoreData.checkpoints.some((checkpoint) => Number.isFinite(Date.parse(checkpoint.checkpointDate)))
      ? trackingMoreData
      : undefined;
  const labelFormat = shippingData.labelUrl?.toLowerCase().endsWith('.zpl') ? 'ZPLII (thermal)' : 'PDF';

  return (
    <Card>
      <CardHeader className="py-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Truck className="h-5 w-5 text-muted-foreground" />
            <CardTitle>Fulfillment Tracking</CardTitle>
            <span className="badge text-white ms-2" style={{ backgroundColor: '#4d148c' }}>
              FEDEX
            </span>
          </div>
          <div className="flex items-center gap-2">
            {trackingNumber && (
              <a
                href={buildFedExTrackingUrl(trackingNumber)}
                target="_blank"
                rel="noreferrer"
                className="btn btn-outline-secondary btn-sm"
              >
                <ExternalLink className="h-4 w-4 me-1" />
                Track on FedEx
              </a>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent>
        <Table className="mb-0" responsive>
          <thead>
            <tr>
              <th className="text-sm text-muted-foreground" colSpan={7}>
                <div className="font-medium mt-2 mb-2">SHIPMENT DETAILS</div>
              </th>
            </tr>
            <tr>
              <th className="text-sm text-muted-foreground border">Provider Order ID</th>
              <th className="text-sm text-muted-foreground border">Tracking Number</th>
              <th className="text-sm text-muted-foreground border">Shipment Status</th>
              <th className="text-sm text-muted-foreground border">Stored Status</th>
              <th className="text-sm text-muted-foreground border">Label Format</th>
              <th className="text-sm text-muted-foreground border">Created At</th>
              <th className="text-sm text-muted-foreground border">Updated At</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="text-sm font-medium border" style={{ color: '#4d148c' }}>
                {shippingData.providerOrderId}
              </td>
              <td className="text-sm font-medium border">{trackingNumber || '-'}</td>
              <td className="text-sm font-medium border">
                <span className="badge bg-secondary">
                  {formatUnifiedShippingStatus(shippingData.unifiedStatus ?? shippingData.status)}
                </span>
              </td>
              <td className="text-sm font-medium border">{shippingData.status || '-'}</td>
              <td className="text-sm font-medium border">{labelFormat}</td>
              <td className="text-sm font-medium border">{formatTimestamp(shippingData.createdAt)}</td>
              <td className="text-sm font-medium border">{formatTimestamp(shippingData.updatedAt)}</td>
            </tr>
          </tbody>
        </Table>

        {shippingData.labelUrl && (
          <div className="mt-3">
            <Button variant="outline-primary" size="sm" onClick={onDownloadLabel} disabled={isDownloadingLabel}>
              <Printer className="h-4 w-4 me-2" />
              {isDownloadingLabel ? 'Preparing...' : 'Print FedEx Labels'}
            </Button>
            <span className="text-xs text-muted-foreground ms-2">
              Includes the original carrier labels and the OMS packing label.
            </span>
            {onDownloadOriginalLabel && (
              <Button variant="link" size="sm" onClick={onDownloadOriginalLabel} disabled={isDownloadingLabel}>
                Download original label
              </Button>
            )}
          </div>
        )}

        {matchingTracking ? (
          <TrackingMoreTimeline tracking={matchingTracking} />
        ) : (
          <Table className="mt-2" responsive>
            <thead>
              <tr>
                <th className="text-sm text-muted-foreground" colSpan={4}>
                  <div className="font-medium mt-4 mb-2">TRACKING HISTORY</div>
                </th>
              </tr>
              <tr>
                <th className="text-sm text-muted-foreground border">Time</th>
                <th className="text-sm text-muted-foreground border">Event</th>
                <th className="text-sm text-muted-foreground border">Description</th>
                <th className="text-sm text-muted-foreground border">FedEx Code</th>
              </tr>
            </thead>
            <tbody>
              {shippingData.events && shippingData.events.length > 0 ? (
                [...shippingData.events]
                  .sort((left, right) => new Date(right.timestamp).getTime() - new Date(left.timestamp).getTime())
                  .map((event, index) => (
                    <tr key={`${event.timestamp}-${event.providerEventCode}-${index}`}>
                      <td className="text-sm font-medium border whitespace-nowrap">
                        {formatTimestamp(event.timestamp)}
                      </td>
                      <td className="text-sm font-medium border">{formatUnifiedShippingStatus(event.event)}</td>
                      <td className="text-sm font-medium border">{event.displayText || event.description || '-'}</td>
                      <td className="text-sm font-medium border">{event.providerEventCode || '-'}</td>
                    </tr>
                  ))
              ) : (
                <tr>
                  <td colSpan={4} className="text-sm text-muted-foreground border">
                    No tracking events yet
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
